import React, { useState, useEffect } from 'react';
import SidePanel from '../util/SidePanel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editProject?: any;
}

const STATUS_OPTIONS = ['active', 'on_hold', 'completed', 'archived'];
const STATUS_LABELS: Record<string, string> = {
  active: 'Active', on_hold: 'On Hold', completed: 'Completed', archived: 'Archived',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col mb-[10px]">
      <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function AddCrmProject({ open, onClose, onSaved, editProject }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [organisations, setOrganisations] = useState<any[]>([]);
  const [projectTypes, setProjectTypes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('active');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [contractRef, setContractRef] = useState('');
  const [color, setColor] = useState('#6366f1');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: orgs }, { data: pts }, { data: usrs }] = await Promise.all([
          axios.get('crm/organisations?active=true'),
          axios.get('crm/project-types'),
          axios.get('users'),
        ]);
        setOrganisations(Array.isArray(orgs) ? orgs : orgs.data ?? []);
        setProjectTypes(Array.isArray(pts) ? pts : []);
        setUsers(Array.isArray(usrs) ? usrs : usrs.data ?? []);
      } catch {}
    })();
  }, [open]);

  useEffect(() => {
    if (editProject) {
      setName(editProject.name ?? '');
      setDescription(editProject.description ?? '');
      setType(editProject.type ?? '');
      setStatus(editProject.status ?? 'active');
      setStartDate(editProject.startDate ? String(editProject.startDate).slice(0, 10) : '');
      setEndDate(editProject.endDate ? String(editProject.endDate).slice(0, 10) : '');
      setOrganisationId(editProject.organisationId ? String(editProject.organisationId) : '');
      setManagerId(editProject.managerId ? String(editProject.managerId) : '');
      setContractValue(editProject.contractValue ? String(editProject.contractValue) : '');
      setContractRef(editProject.contractRef ?? '');
      setColor(editProject.color ?? '#6366f1');
    } else {
      setName(''); setDescription(''); setType(''); setStatus('active');
      setStartDate(''); setEndDate(''); setOrganisationId(''); setManagerId('');
      setContractValue(''); setContractRef(''); setColor('#6366f1');
    }
  }, [editProject, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      enqueueSnackbar(t('Required'), { variant: 'error' });
      return;
    }
    const payload: any = {
      name: name.trim(),
      description: description.trim() || null,
      type: type || null,
      status,
      startDate: startDate || null,
      endDate: endDate || null,
      organisationId: organisationId ? Number(organisationId) : null,
      managerId: managerId ? Number(managerId) : null,
      contractValue: contractValue ? Number(contractValue) : null,
      contractRef: contractRef.trim() || null,
      color,
    };
    try {
      if (editProject?.id) {
        await axios.put(`crm/projects/${editProject.id}`, payload);
        enqueueSnackbar('Project updated', { variant: 'success' });
      } else {
        await axios.post('crm/projects', payload);
        enqueueSnackbar('Project created', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const inp = (value: string, set: (v: string) => void, type = 'text', label = '') => (
    <input
      title={label || type}
      placeholder={label}
      className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
      type={type} value={value} onChange={(e) => set(e.target.value)}
    />
  );

  return (
    <SidePanel open={open} onClose={onClose} width={460} paperStyle={{ maxWidth: '100vw', display: 'flex', flexDirection: 'column' }}>

      <div className="flex items-center justify-between px-[20px] py-[13px] flex-shrink-0" style={{ borderBottom: 'var(--border-input)' }}>
        <span className="text-[16px] font-semibold text-[var(--text-color)]">
          {editProject ? t('Edit') : t('AddProject')}
        </span>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--text-color)', opacity: 0.6 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="flex-1 overflow-y-auto px-[20px] py-[16px]">

        <Field label={t('Name')}>{inp(name, setName, 'text', t('Name'))}</Field>

        <div className="grid grid-cols-2 gap-x-[10px]">
          <Field label={t('ProjectPurpose')}>
            <select className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
              value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">—</option>
              {projectTypes.map((pt) => <option key={pt.id} value={pt.slug}>{pt.name}</option>)}
            </select>
          </Field>
          <Field label={t('Status')}>
            <select className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
              value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Responsible">
          <select className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
            value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            <option value="">— Not assigned —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
            ))}
          </select>
        </Field>

        <Field label={t('Organisations')}>
          <select className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
            value={organisationId} onChange={(e) => setOrganisationId(e.target.value)}>
            <option value="">—</option>
            {organisations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-x-[10px]">
          <Field label={t('StartDate')}>{inp(startDate, setStartDate, 'date', t('StartDate'))}</Field>
          <Field label={t('EndDate')}>{inp(endDate, setEndDate, 'date', t('EndDate'))}</Field>
        </div>

        <div className="grid grid-cols-2 gap-x-[10px]">
          <Field label={t('ContractValue')}>{inp(contractValue, setContractValue, 'number', t('ContractValue'))}</Field>
          <Field label={t('ContractRef')}>{inp(contractRef, setContractRef, 'text', t('ContractRef'))}</Field>
        </div>

        <Field label={t('Description')}>
          <textarea
            className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
            rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <div className="flex items-center gap-[10px] mb-[10px]">
          <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] text-[var(--text-color-calendar-week)]">Color</label>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
            className="w-[32px] h-[24px] cursor-pointer rounded border-none" />
          <span className="text-[12px] text-[var(--text-color)]">{color}</span>
        </div>
      </div>

      <div className="flex-shrink-0 px-[20px] py-[14px] flex gap-[10px]" style={{ borderTop: 'var(--border-input)' }}>
        <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={handleSave}>
          {editProject ? t('Change') : t('Add')}
        </Button>
        <Button variant="outlined" sx={{ flex: 1, fontSize: 13, color: 'var(--text-color)', borderColor: 'var(--border-input)' }} onClick={onClose}>
          {t('Cancel')}
        </Button>
      </div>
    </SidePanel>
  );
}
